// Alert sources, the model provider, and the setup a workspace has left (#422).
//
// Read off `routes/sources.py`. A source's `status` is what the receiver already
// records: `waiting` until the customer's own subscription confirms (an accepted
// receipt with a publishing account), `confirmed` after, `disabled` when turned
// off. A deleted source is not listed (#816). "Send test alert" opens an investigation marked
// as a test, and is not a delivery (#876). The provider's key is sent once and never comes
// back; `has_key` is all a read says about it.

import type { Api } from "./http";

export const SOURCES_PATH = "/api/v1/alert-sources";
export const PROVIDER_PATH = "/api/v1/provider";
export const SETUP_PATH = "/api/v1/setup";

export type SourceType = "cloudwatch_sns" | "generic_hmac" | "alertmanager" | "pagerduty";

export const SOURCE_TYPES: ReadonlyArray<[SourceType, string, string]> = [
  ["cloudwatch_sns", "CloudWatch alarms (SNS)", "An SNS topic in your account subscribes to the source's URL; alarms publish to the topic."],
  ["alertmanager", "Prometheus Alertmanager", "A webhook receiver with a shared secret; the URL and secret go in your Alertmanager config."],
  ["generic_hmac", "Generic webhook (HMAC)", "Any sender that can sign a request: HMAC-SHA256 over the timestamp and the body."],
  ["pagerduty", "PagerDuty", "A PagerDuty webhook subscription, signed with the shared secret."],
];

export interface SourceRow {
  id: string;
  name: string;
  source_type: SourceType;
  description: string;
  is_active: boolean;
  auto_investigate: boolean;
  url: string;
  confirmed_accounts: string[];
  status: "waiting" | "confirmed" | "disabled";
  alert_count: number;
  last_alert_at: string | null;
  last_received_at: string | null;
  refused: number;
  /** Alarms on this source the spend gate refused an investigation (#1354). */
  refused_by_spend?: number;
  /** Alarms the source's filter skipped in the last seven days (#847). */
  skipped?: number;
  /** The end of the source's dry run while one lasts (#849); null otherwise. */
  dry_run_until?: string | null;
  created_at: string;
  /** Once, on creation, for the kinds a sender signs with. */
  secret?: string;
}

// Which alarms start an investigation (#847): the source's filter, read off
// `ingest/alert_filter.py::AlertFilter`. The server validates every write; the
// evaluation order is that module's.

export type FilterMode = "all_except_noise" | "opt_in";

export interface FilterRule {
  field: "name" | "namespace";
  action: "include" | "exclude";
  pattern: string;
}

export interface FilterSettings {
  mode: FilterMode;
  noise: { target_tracking: boolean; state_transitions: boolean; muted: boolean };
  rules: FilterRule[];
  accounts_off: string[];
  regions_off: string[];
}

export type SkipReason =
  | "account_off"
  | "region_off"
  | "muted"
  | "state_transition"
  | "target_tracking"
  | "excluded_by_name"
  | "excluded_by_namespace"
  | "not_opted_in";

export interface SkippedAlarm {
  received_at: string;
  alarm: string;
  /** A code: shown only through `skipReasonLabel`. */
  reason: string;
  detail: string;
  account_id: string;
  region: string;
}

/** An alarm a dry run held (#849): one row per alarm, its refires counted on it. */
export interface WouldHaveInvestigated {
  held_at: string;
  alarm: string;
  severity: string;
  occurrences: number;
  last_seen_at: string;
  account_id: string;
  region: string;
}

export interface DryRunRead {
  /** The end of the dry run while one lasts; null when the source is live. */
  until: string | null;
  /** Every alarm a dry run held on this source; the list shows the newest of them. */
  total: number;
  would_have_investigated: WouldHaveInvestigated[];
}

export interface SourceFilterRead {
  settings: FilterSettings;
  /** The accounts a switch can name: the workspace's connected accounts. */
  accounts: Array<{ account_id: string; name: string | null }>;
  regions: string[];
  skipped: SkippedAlarm[];
  dry_run?: DryRunRead;
}

/** A dry run offered at setup is a week, and lasts at most a month (`ingest/holds.py`). */
export const DEFAULT_DRY_RUN_DAYS = 7;
export const MAX_DRY_RUN_DAYS = 30;

export const MODES: ReadonlyArray<[FilterMode, string, string]> = [
  ["all_except_noise", "Every alarm except known noise", "Each alarm starts an investigation unless the noise list or a pattern skips it."],
  ["opt_in", "Only alarms I opt in", "An alarm starts an investigation only when an include pattern names it."],
];

export const NOISE: ReadonlyArray<[keyof FilterSettings["noise"], string, string]> = [
  ["target_tracking", "Autoscaling's own target-tracking alarms", "The alarms AWS creates for a target-tracking scaling policy. They fire as the policy scales."],
  ["state_transitions", "OK and INSUFFICIENT_DATA transitions", "Listed here, and they start nothing. An OK still closes the alarm it follows."],
  // Only the EventBridge form of an alarm carries its mute detail (#1359).
  ["muted", "Alarms muted in CloudWatch", "An alarm a CloudWatch mute rule covers when it changes state. Applies to alarms that arrive through an account connection (template 3.4.0 and later). An alarm action wired to your own SNS topic carries no mute details, so it can't be filtered this way."],
];

/** Why an alarm was skipped, in words. Never the code: an unknown one reads as a rule. */
export function skipReasonLabel(skip: Pick<SkippedAlarm, "reason" | "detail">): string {
  switch (skip.reason) {
    case "account_off":
      return `Account ${skip.detail} is switched off`;
    case "region_off":
      return `Region ${skip.detail} is switched off`;
    case "muted":
      return "Muted in CloudWatch";
    case "state_transition":
      return `${skip.detail} transition, which starts nothing`;
    case "target_tracking":
      return "Autoscaling's own target-tracking alarm";
    case "excluded_by_name":
      return `Name matches the exclude pattern ${skip.detail}`;
    case "excluded_by_namespace":
      return `Namespace matches the exclude pattern ${skip.detail}`;
    case "not_opted_in":
      return "No include pattern names it";
    default:
      return "Skipped by this source's filter";
  }
}

export const readSourceFilter = (api: Api, id: string): Promise<SourceFilterRead> =>
  api.get<SourceFilterRead>(`${SOURCES_PATH}/${encodeURIComponent(id)}/filter`);

export const saveSourceFilter = (api: Api, id: string, settings: FilterSettings): Promise<SourceFilterRead> =>
  api.put<SourceFilterRead>(`${SOURCES_PATH}/${encodeURIComponent(id)}/filter`, settings);

/** Start the source's dry run for `days` from now, or end it now with 0 (#849). */
export const setSourceDryRun = (api: Api, id: string, days: number): Promise<SourceFilterRead> =>
  api.put<SourceFilterRead>(`${SOURCES_PATH}/${encodeURIComponent(id)}/dry-run`, { days });

export interface ProviderRow {
  id: string;
  provider: "openai" | "litellm";
  api_base: string;
  high_model_id: string;
  low_model_id: string | null;
  has_key: boolean;
  created_at: string;
}

/**
 * When the workspace's current provider last refused one of its investigations (#1415): the
 * run, when its refusal was recorded, the kind (`quota`, `auth` or `model`), and the
 * runtime's own sentence. History, not the provider's state now.
 */
export interface ProviderRefusal {
  run_id: string;
  recorded_at: string;
  /** Null for a kind the server does not name. */
  kind: "quota" | "auth" | "model" | null;
  sentence: string;
}

/** `GET /api/v1/provider`: the active provider, and its last refusal; either may be null. */
export interface ProviderRead {
  provider: ProviderRow | null;
  last_refusal: ProviderRefusal | null;
}

export interface ProviderCheck {
  models: number;
  found: Record<string, boolean>;
  /** Why an id counts as found though not listed as configured (#1357), by model id. */
  notes: Record<string, string>;
  sample: string[];
}

export interface Setup {
  account_connected: boolean;
  alert_source_created: boolean;
  alert_source_confirmed: boolean;
  provider_set: boolean;
  first_run_id: string | null;
}

export const listSources = (api: Api): Promise<SourceRow[]> => api.get<SourceRow[]>(SOURCES_PATH);

export interface NewSource {
  name: string;
  source_type: SourceType;
  description?: string;
  /** Start in a dry run of this many days (#849); absent or 0 starts it live. */
  dry_run_days?: number;
}

export const createSource = (api: Api, body: NewSource): Promise<SourceRow> => api.post<SourceRow>(SOURCES_PATH, body);

export const setSourceActive = (api: Api, id: string, active: boolean): Promise<void> =>
  api.post<void>(`${SOURCES_PATH}/${encodeURIComponent(id)}/${active ? "enable" : "disable"}`);

/** Whether an alarm on the source starts an investigation (#816); answers the listed row. */
export const setSourceAutoInvestigate = (api: Api, id: string, on: boolean): Promise<SourceRow> =>
  api.patch<SourceRow>(`${SOURCES_PATH}/${encodeURIComponent(id)}`, { auto_investigate: on });

/** Delete a source (#816): its endpoint refuses alerts from then on, and it leaves the list. */
export const deleteSource = (api: Api, id: string): Promise<void> =>
  api.delete<void>(`${SOURCES_PATH}/${encodeURIComponent(id)}`);

/** What "Send test alert" answered (#876): the test's investigation, and whether an earlier
 * test's already held it. `run_id` is null when the alarm arrived and none was opened. */
export interface TestAlertSent {
  run_id: string | null;
  joined: boolean;
}

/** Send the source's sample alarm through its own path (#876). */
export const sendTestAlert = (api: Api, id: string): Promise<TestAlertSent> =>
  api.post<TestAlertSent>(`${SOURCES_PATH}/${encodeURIComponent(id)}/test-alert`);

export const readProvider = (api: Api): Promise<ProviderRead> => api.get<ProviderRead>(PROVIDER_PATH);

export const setProvider = (
  api: Api,
  body: {
    provider: "openai" | "litellm";
    api_base: string;
    api_key: string;
    high_model_id: string;
    low_model_id: string | null;
  },
): Promise<ProviderRow> => api.put<ProviderRow>(PROVIDER_PATH, body);

export const checkProvider = (api: Api): Promise<ProviderCheck> =>
  api.post<ProviderCheck>(`${PROVIDER_PATH}/check`);

export const readSetup = (api: Api): Promise<Setup> => api.get<Setup>(SETUP_PATH);

/** What is left before the first investigation, in the order a person does it. */
export function setupSteps(setup: Setup): Array<{ done: boolean; label: string; href: string }> {
  return [
    { done: setup.account_connected, label: "Connect an AWS account", href: "/accounts" },
    {
      done: setup.alert_source_confirmed,
      label: setup.alert_source_created
        ? "Confirm your alert source's subscription"
        : "Add an alert source",
      href: "/sources",
    },
    { done: setup.provider_set, label: "Set a model provider", href: "/model-provider" },
  ];
}
